import FormTextarea from '@c/forms/controls/FormTextarea';
import ProductViewerModal from '@c/modals/ProductViewerModal';
import { StarIcon } from '@heroicons/react/20/solid';
import bolt from '@i/profileHome/bolt.svg';
import boxx from '@i/profileHome/boxx.svg';
import fire from '@i/profileHome/fire.svg';
import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import {
  removeReply,
  removeReview,
  submitReply,
} from '@util/firestore/reviews';
import { logError } from '@util/logError';
import { useAuth } from 'context/AuthContext';
import { useToastContext } from 'context/ToastContext';
import { AnimatePresence } from 'framer-motion';
import { ReviewDocument } from 'models/review';
import { MotionDiv } from 'motion';
import { useState } from 'react';

export const ReviewCard = ({
  review,
  productIds,
}: {
  review: ReviewDocument;
  productIds?: string[];
}) => {
  const { userDoc } = useAuth();
  const [removing, setRemoving] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [initialImage, setInitialImage] = useState(0);
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [reply, setReply] = useState('');
  const { showErrorToast, showSuccessToast } = useToastContext();
  const [loadingReply, setLoadingReply] = useState(false);
  const [removingReply, setRemovingReply] = useState(0); // set to created to only remove the one that is being deleted
  const [replies, setReplies] = useState(review.replies ?? []); // to show updates without needing to refresh since they are server side fetched -> not sure if they need to be server side fetched but could be nice for SEO so I'm leaving it
  return (
    <div className="bg-white p-[2.4rem]">
      <div className="flex justify-between">
        <div className="flex">
          <SafeImage
            src={review.reviewer_photo}
            key={review.reviewer_photo}
            alt="header image"
            className="h-[4.4rem] w-[4.4rem] rounded-full"
          />
          <div className="ml-[1.6rem] flex flex-col ">
            <span className="text-[1.6rem] text-black">
              {review.reviewer_username}
            </span>
            {/* <span className="text-[1.4rem] text-brand-extreme-lightest-black">
                {user?.created &&
                  new Date(user?.created).toLocaleDateString('en-US', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
              </span> */}
          </div>
        </div>
        <div className="flex flex-col items-center sm:flex-row">
          <div className="flex gap-[0.8rem]">
            {review.fast_shipper && (
              <SafeImage
                src={bolt}
                alt="header image"
                className="h-[2.4rem] w-[2.4rem]"
              />
            )}
            {review.as_described && (
              <SafeImage
                src={boxx}
                alt="header image"
                className="h-[2.4rem] w-[2.4rem]"
              />
            )}
            {review.quick_replies && (
              <SafeImage
                src={fire}
                alt="header image"
                className="h-[2.4rem] w-[2.4rem]"
              />
            )}
          </div>
          <div className="ml-[1.6rem] mt-[0.2rem] flex gap-[.5rem] sm:mt-0">
            {[...Array(review.rating)].map((_, index) => (
              <StarIcon width={24} height={24} fill="#C41719" key={index} />
            ))}
            {[...Array(5 - review.rating)].map((_, index) => (
              <StarIcon width={24} height={24} key={index} fill="#808080" />
            ))}
          </div>
        </div>
      </div>

      <div>
        {review.products?.map((item) => {
          if (productIds?.length && !productIds.includes(item.id)) return null;
          return (
            <div
              className="mt-[1.5rem] flex md:grid md:grid-cols-8"
              key={item.id}
            >
              <SafeImage
                src={item.thumbnail}
                alt={item.title}
                className="h-[6.4rem] w-[6.4rem] lg:ml-[1.2rem]"
              />
              <div className="col-span-2 ml-[1.2rem] flex flex-col md:col-span-7 ">
                <span className="text-[1.6rem] font-semibold text-black">
                  {item.title}
                </span>
                <span className="text-[1.4rem] font-normal text-brand-mid-gray">
                  {item.category}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-[2rem] text-[1.6rem] font-normal text-brand-extreme-lightest-black">
        {review.comment}
      </div>
      <div className="flex gap-4">
        <ProductViewerModal
          images={review.images ?? []}
          initialImage={initialImage}
          isOpen={openModal}
          dismiss={() => setOpenModal(false)}
        />
        {review.images?.map((image, index) => (
          <SafeImage
            key={image.full}
            src={image.full}
            width={100}
            height={100}
            className="cursor-pointer"
            alt={review.comment}
            onClick={() => {
              setInitialImage(index);
              setOpenModal(true);
            }}
          />
        ))}
      </div>
      {replies?.map((reply) => (
        <MotionDiv
          key={reply.comment}
          initial={{ height: 0 }}
          transition={{
            x: { type: 'spring', bounce: 0 },
            opacity: { duration: 0.5 },
          }}
          animate={{ height: 'auto', overflow: 'hidden' }}
          exit={{ height: 0, overflow: 'hidden' }}
          className="ml-[3.2rem] mt-4 rounded-lg bg-brand-lightest-gray p-4"
        >
          <div className="flex w-full items-center gap-4">
            <SafeImage
              src={reply.photo}
              width={40}
              height={40}
              className="rounded-full"
              alt={reply.username}
            />
            <span className="text-[1.6rem] font-semibold">
              {reply.username}
            </span>
            <span className="grow"></span>
            {(reply.uid === userDoc?.uid || userDoc?.roles?.support) &&
              removingReply !== reply.created && (
                <button
                  className="text-[1.6rem] font-semibold text-brand-secondary underline"
                  onClick={async () => {
                    if (
                      window.confirm(
                        'Are you sure you want to delete this reply?'
                      )
                    ) {
                      try {
                        setRemovingReply(reply.created);
                        await removeReply({
                          reviewId: review.id,
                          reply,
                        });
                        setReplies((prev) =>
                          prev.filter((item) => item.created !== reply.created)
                        ),
                          showSuccessToast('Reply deleted successfully.');
                      } catch (e) {
                        logError(e);
                        showErrorToast(
                          'Failed to delete reply. Please contact support.'
                        );
                      } finally {
                        setRemovingReply(0);
                      }
                    }
                  }}
                >
                  Delete
                </button>
              )}
            {removingReply === reply.created && <span>Deleting...</span>}
          </div>
          <div className="mt-4 text-[1.6rem] font-normal">{reply.comment}</div>
        </MotionDiv>
      ))}
      <div className="mt-4 flex items-center gap-4">
        {userDoc?.roles?.support && (
          <>
            <div>
              <Button
                type="secondary"
                text="Remove Review"
                loading={removing}
                onClick={async () => {
                  if (
                    window.confirm(
                      'Are you sure you want to remove this review?'
                    )
                  ) {
                    setRemoving(true);
                    await removeReview(review.id);
                    setRemoving(false);
                    window.location.reload();
                  }
                }}
              />
            </div>
            <div>
              <Button
                text="View Order"
                href={`/dashboard/purchases/${review.order_id}`}
                target="_blank"
              />
            </div>
          </>
        )}
        {!!userDoc &&
          (userDoc.uid === review.uid || userDoc.roles?.support) && (
            <div className="flex w-full justify-end">
              <Button
                text="Reply"
                type="tertiary"
                onClick={() => {
                  setShowReplyBox(true);
                }}
                height="small"
              />
            </div>
          )}
      </div>
      <AnimatePresence>
        {showReplyBox && (
          <MotionDiv
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0, overflow: 'hidden' }}
            className="mt-4 flex flex-col items-end gap-4"
          >
            <FormTextarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
            <div className="flex gap-4">
              <Button
                text="Cancel"
                onClick={() => setShowReplyBox(false)}
                type="tertiary"
              />

              <Button
                text="Submit"
                width="fixed"
                loading={loadingReply}
                onClick={async () => {
                  if (!userDoc || loadingReply) return;
                  try {
                    setLoadingReply(true);
                    await submitReply({
                      reviewId: review.id,
                      comment: reply,
                      username: userDoc.username,
                      uid: userDoc.uid,
                      photo: userDoc.photo,
                    });
                    setReplies((prev) => [
                      ...prev,
                      {
                        comment: reply,
                        username: userDoc.username,
                        uid: userDoc.uid,
                        photo: userDoc.photo,
                        created: Date.now(),
                      },
                    ]),
                      showSuccessToast('Reply submitted successfully.');
                    setShowReplyBox(false);
                  } catch (e) {
                    logError(e);
                    showErrorToast(
                      'Failed to submit reply. Please contact support.'
                    );
                  } finally {
                    setLoadingReply(false);
                  }
                }}
              />
            </div>
          </MotionDiv>
        )}
      </AnimatePresence>
    </div>
  );
};
